import React, { useEffect, useState } from 'react';
import { CheckCircle, AlertCircle, Loader, Mail, ArrowRight } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const VerificationPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error' | 'expired'
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  // Auto-verify on component mount
  useEffect(() => {
    const verifyToken = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage('No verification token provided');
        return;
      }

      try {
        // Simulate API call to verify token
        await new Promise((resolve) => setTimeout(resolve, 1500));

        // Mock verification logic
        if (token.length >= 20) {
          // Simulate successful verification
          // In production, this would be an actual API call
          setEmail('user@example.com');
          setStatus('success');
          setMessage('Your email has been verified successfully!');
        } else {
          setStatus('error');
          setMessage('Invalid verification token');
        }
      } catch (error) {
        setStatus('error');
        setMessage('Verification failed. Please try again later.');
      }
    };

    verifyToken();
  }, [searchParams]);

  // Determine card styling based on status
  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return '#10b981'; // Green
      case 'error':
        return '#ef4444'; // Red
      case 'expired':
        return '#f59e0b'; // Amber
      default:
        return '#0095fa'; // Blue
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircle size={48} className="text-white" />;
      case 'error':
      case 'expired':
        return <AlertCircle size={48} className="text-white" />;
      default:
        return <Loader size={48} className="text-white animate-spin" />;
    }
  };

  const getStatusTitle = () => {
    switch (status) {
      case 'success':
        return 'Email Verified!';
      case 'error':
        return 'Verification Failed';
      case 'expired':
        return 'Link Expired';
      default:
        return 'Verifying Your Email';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #0095fa 0%, #0f6caa 100%)' }}>
      {/* Decorative elements */}
      <div className="fixed top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
        {/* Top right accent */}
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full" style={{ backgroundColor: '#ffffff', opacity: 0.1 }} />
        {/* Bottom left accent */}
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full" style={{ backgroundColor: '#ffffff', opacity: 0.08 }} />
      </div>

      {/* Verification Card */}
      <div className="relative z-10 w-full max-w-sm mt-10 pt-10" >
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-10 space-y-6" style={{ boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)' }}>
          {/* Status Icon Circle */}
          <div className="flex justify-center">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{ backgroundColor: getStatusColor() }}
            >
              {getStatusIcon()}
            </div>
          </div>

          {/* Status Title */}
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: 'Montserrat' }}>
              {getStatusTitle()}
            </h1>
            <p className="text-gray-600 text-sm">
              {message}
            </p>
          </div>

          {/* Email Display (if success or error with email) */}
          {email && (
            <div className="flex items-center justify-center gap-2 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <Mail size={18} style={{ color: '#0095fa' }} />
              <p className="text-sm text-gray-700 font-semibold">{email}</p>
            </div>
          )}

          {/* Status-specific content */}
          {status === 'verifying' && (
            <div className="text-center space-y-2">
              <p className="text-gray-600 text-sm">
                Please wait while we verify your email...
              </p>
              <div className="flex justify-center gap-1">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
              </div>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-4">
              <div className="text-center space-y-2">
                <p className="text-gray-600 text-sm">
                  Your account is now fully activated. You can now sign in to your account.
                </p>
              </div>

              {/* Next Steps */}
              <div className="bg-green-50 rounded-lg p-4 space-y-3 border border-green-200">
                <p className="text-sm font-semibold text-gray-800" style={{ fontFamily: 'Montserrat' }}>
                  What's next?
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold mt-0.5">✓</span>
                    <span>Email verified</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold mt-0.5">✓</span>
                    <span>Account activated</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold mt-0.5">→</span>
                    <span>Sign in to get started</span>
                  </li>
                </ul>
              </div>

              {/* Sign In Button */}
              <a
                href="/login"
                className="block w-full py-3 rounded-lg font-semibold text-white transition-all duration-300 flex items-center justify-center gap-2 text-center"
                style={{
                  backgroundColor: '#0095fa',
                  boxShadow: '0 5px 15px rgba(0, 149, 250, 0.4)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#0f6caa';
                  e.currentTarget.style.boxShadow = '0px 0px 20px rgba(0, 149, 250, 0.8)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#0095fa';
                  e.currentTarget.style.boxShadow = '0 5px 15px rgba(0, 149, 250, 0.4)';
                }}
              >
                Sign In to Your Account
                <ArrowRight size={18} />
              </a>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <p className="text-sm text-red-700">
                  The verification link appears to be invalid or may have expired. Please request a new verification email.
                </p>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <a
                  href="/signup"
                  className="block w-full py-3 rounded-lg font-semibold text-white transition-all duration-300 text-center"
                  style={{
                    backgroundColor: '#0095fa',
                    boxShadow: '0 5px 15px rgba(0, 149, 250, 0.4)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#0f6caa';
                    e.currentTarget.style.boxShadow = '0px 0px 20px rgba(0, 149, 250, 0.8)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#0095fa';
                    e.currentTarget.style.boxShadow = '0 5px 15px rgba(0, 149, 250, 0.4)';
                  }}
                >
                  Request New Verification Email
                </a>

                <a
                  href="/"
                  className="block w-full py-3 rounded-lg font-semibold text-gray-700 transition-all duration-300 text-center border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                >
                  Go to Home
                </a>
              </div>
            </div>
          )}

          {status === 'expired' && (
            <div className="space-y-4">
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <p className="text-sm text-amber-700">
                  This verification link has expired. Verification links are valid for 24 hours only.
                </p>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <a
                  href="/signup"
                  className="block w-full py-3 rounded-lg font-semibold text-white transition-all duration-300 text-center"
                  style={{
                    backgroundColor: '#0095fa',
                    boxShadow: '0 5px 15px rgba(0, 149, 250, 0.4)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#0f6caa';
                    e.currentTarget.style.boxShadow = '0px 0px 20px rgba(0, 149, 250, 0.8)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#0095fa';
                    e.currentTarget.style.boxShadow = '0 5px 15px rgba(0, 149, 250, 0.4)';
                  }}
                >
                  Request New Verification Email
                </a>

                <a
                  href="/login"
                  className="block w-full py-3 rounded-lg font-semibold text-gray-700 transition-all duration-300 text-center border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                >
                  Back to Login
                </a>
              </div>
            </div>
          )}

          {/* Footer Help Text */}
          <div className="text-center">
            <p className="text-xs text-gray-500" style={{ fontFamily: 'Montserrat' }}>
              Need help?{' '}
              <a href="/contact" className="text-blue-600 hover:text-blue-700 font-semibold transition-colors">
                Contact Support
              </a>
            </p>
          </div>
        </div>

        {/* Additional Info Card */}
        <div className="mt-6 bg-white bg-opacity-95 rounded-lg p-4 shadow-lg backdrop-blur-sm">
          <p className="text-xs text-gray-600 text-center" style={{ fontFamily: 'Montserrat' }}>
            <span className="font-semibold">Pro Tip:</span> Make sure to check your spam folder if you don't receive verification emails.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerificationPage;